import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import ExportBar from "../../Components/Accounting/ExportBar";
import Select from "react-select";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const ContactStatement = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [contact,   setContact]   = useState(null);
  const [statement, setStatement] = useState(null);
  const [contacts,  setContacts]  = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [fromDate,  setFromDate]  = useState("");
  const [toDate,    setToDate]    = useState("");

  useEffect(() => {
    fetchContacts();
    if (id) {
      setSelected({ value: parseInt(id) });
    }
  }, [id]);

  useEffect(() => {
    if (selected?.value) fetchStatement(selected.value);
  }, [selected, fromDate, toDate]);

  const fetchContacts = async () => {
    const res = await ajaxAccounting.listContacts({});
    if (res.status === "OK") {
      const list = Array.isArray(res.details) ? res.details : res.details?.list || [];
      setContacts(list);
      if (id) {
        const found = list.find((c) => c.id === parseInt(id));
        if (found) setSelected({ value: found.id, label: found.name });
      }
    }
  };

  const fetchStatement = async (contactId) => {
    setLoading(true);
    const res = await ajaxAccounting.getContactStatement({
      contact_id: contactId,
      from_date:  fromDate || undefined,
      to_date:    toDate   || undefined,
    });
    if (res.status === "OK") {
      setContact(res.details?.contact  || null);
      setStatement(res.details?.statement || res.details || null);
    } else toast.error(res.message);
    setLoading(false);
  };

  const contactOptions = contacts.map((c) => ({
    value: c.id,
    label: `${c.name} (${c.type})`,
  }));

  const transactions = statement?.transactions || [];
  const openingBal   = statement?.opening_balance || 0;
  const closingBal   = statement?.closing_balance || 0;
  const totalDr      = transactions.reduce((s, t) => s + (parseFloat(t.debit)  || 0), 0);
  const totalCr      = transactions.reduce((s, t) => s + (parseFloat(t.credit) || 0), 0);

  const exportColumns = [
    { key:"date",        label:"Date" },
    { key:"reference",   label:"Reference" },
    { key:"description", label:"Description" },
    { key:"source_type", label:"Type" },
    { key:"debit",       label:"Debit" },
    { key:"credit",      label:"Credit" },
    { key:"balance",     label:"Balance" },
  ];

  return (
    <AppContainer
      title="Contact Statement"
      previousPageTitle="Contacts"
      description="View all transactions associated with a customer or supplier — invoices raised, payments received or made, and the running balance."
      actions={[
        { label:"Print Statement", icon:"fa-solid fa-print", className:"btn btn-outline-secondary", onClick: () => window.print() },
        { label:"Back to Contacts", icon:"fa-solid fa-arrow-left", className:"btn btn-light", onClick:() => navigate("/accounting/contacts") },
      ]}
    >
      <Toaster />

      {/* Filters */}
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-5">
            <label className="form-label tx-12 fw-semibold">Contact</label>
            <Select
              options={contactOptions}
              value={selected}
              onChange={(v) => { setSelected(v); if (v) navigate(`/accounting/contacts/${v.value}/statement`); }}
              placeholder="Search contact..."
              isSearchable
            />
          </div>
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">From Date</label>
            <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">To Date</label>
            <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>
      </div>

      {!selected && (
        <div className="card custom-card p-5 text-center tx-muted">
          <i className="fa-solid fa-address-book" style={{ fontSize:"3rem", opacity:0.3 }}></i>
          <p className="mt-3">Select a contact to view their statement.</p>
        </div>
      )}

      {selected && !loading && contact && (
        <>
          {/* Contact header */}
          <div className="card custom-card p-4 mb-3">
            <div className="row g-3">
              <div className="col-lg-6">
                <h5 className="fw-bold mb-1">{contact.name}</h5>
                <div className="d-flex gap-2 flex-wrap">
                  <span className={`badge ${contact.type === "customer" ? "bg-success" : contact.type === "supplier" ? "bg-primary" : "bg-info"}`}>{contact.type}</span>
                  {contact.email && <span className="tx-12 tx-muted"><i className="fa-solid fa-envelope me-1"></i>{contact.email}</span>}
                  {contact.phone && <span className="tx-12 tx-muted"><i className="fa-solid fa-phone me-1"></i>{contact.phone}</span>}
                </div>
              </div>
              <div className="col-lg-6">
                <div className="row g-2">
                  {[
                    { label:"Opening Balance", value: openingBal, color:"#6366f1" },
                    { label:"Total Debits",    value: totalDr,    color:"#10b981" },
                    { label:"Total Credits",   value: totalCr,    color:"#ef4444" },
                    { label:"Closing Balance", value: closingBal, color: closingBal > 0 ? "#f59e0b" : "#10b981" },
                  ].map((c, i) => (
                    <div key={i} className="col-6">
                      <div className="p-2 rounded" style={{ border:`1px solid ${c.color}30`, background: c.color + "08" }}>
                        <div className="tx-11 tx-muted">{c.label}</div>
                        <div className="fw-bold tx-14" style={{ color: c.color }}>{functions.formatCurrency(c.value)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Transactions table */}
          <div className="card custom-card p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-semibold mb-0">Transaction History</h6>
              <ExportBar
                data={transactions}
                columns={exportColumns}
                filename={`statement-${contact.name?.replace(/\s+/g,"-")}`}
                pdfTitle={`Statement — ${contact.name}`}
                printCode="" exportCode=""
              />
            </div>

            <div className="table-responsive">
              <table className="table table-bordered table-sm">
                <thead className="bg-light">
                  <tr>
                    <th>Date</th>
                    <th>Reference</th>
                    <th>Description</th>
                    <th>Type</th>
                    <th className="text-end">Debit</th>
                    <th className="text-end">Credit</th>
                    <th className="text-end">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Opening balance row */}
                  <tr className="bg-light">
                    <td colSpan={4}><strong>Opening Balance</strong></td>
                    <td colSpan={2}></td>
                    <td className="text-end fw-bold">{functions.formatCurrency(openingBal)}</td>
                  </tr>

                  {transactions.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-3 tx-muted">No transactions in this period.</td></tr>
                  )}

                  {transactions.map((t, i) => {
                    const typeLabelMap = {
                      invoice_payment: { label:"Payment",      color:"bg-success" },
                      invoice:         { label:"Invoice",      color:"bg-primary" },
                      journal:         { label:"Journal",      color:"bg-secondary" },
                      voucher:         { label:"Voucher",      color:"bg-info" },
                    };
                    const typeInfo = typeLabelMap[t.source_type] || { label: t.source_type || "Entry", color:"bg-secondary" };
                    return (
                      <tr key={i}>
                        <td className="tx-12">{functions.formatDate(t.date)}</td>
                        <td><code className="tx-11">{t.reference || "—"}</code></td>
                        <td className="tx-12">{t.description}</td>
                        <td><span className={`badge ${typeInfo.color}`} style={{ fontSize:"0.7rem" }}>{typeInfo.label}</span></td>
                        <td className="text-end tx-success">{t.debit > 0 ? functions.formatCurrency(t.debit) : "—"}</td>
                        <td className="text-end tx-danger">{t.credit > 0 ? functions.formatCurrency(t.credit) : "—"}</td>
                        <td className={`text-end fw-bold ${t.balance < 0 ? "tx-danger" : ""}`}>{functions.formatCurrency(Math.abs(t.balance || 0))}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-light fw-semibold">
                  <tr>
                    <td colSpan={4}>Totals</td>
                    <td className="text-end tx-success">{functions.formatCurrency(totalDr)}</td>
                    <td className="text-end tx-danger">{functions.formatCurrency(totalCr)}</td>
                    <td className="text-end">{functions.formatCurrency(Math.abs(closingBal))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {closingBal > 0 && (
              <div className="alert alert-warning mt-3 tx-13">
                <i className="fa-solid fa-triangle-exclamation me-1"></i>
                <strong>Outstanding balance: {functions.formatCurrency(closingBal)}</strong>
                {contact.type === "customer" ? " — Customer owes this amount." : " — You owe this supplier."}
                {contact.credit_terms && <span className="ms-2 tx-muted">Terms: {contact.credit_terms}</span>}
              </div>
            )}
          </div>
        </>
      )}

      {loading && (
        <div className="card custom-card p-5 text-center">
          <i className="fa-solid fa-spinner fa-spin fa-2x tx-primary"></i>
          <p className="mt-2 tx-muted">Loading statement...</p>
        </div>
      )}
    </AppContainer>
  );
};

export default ContactStatement;
